import type { ReactElement } from 'react';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/toast';

import { stageActErrorCopy } from '../act-errors';
import { stageName } from '../action-presentation';
import type { EditorModal } from '../editor-modal';
import type { FlowCapabilities } from '../types';
import type { FlowActions } from '../use-flow-actions';
import { ReturnModal } from './ReturnModal';

// Confirmaciones de los actos de G2 (G2-2, G2-3): la conformidad del revisor asignado,
// el cierre de la aprobación por Calidad y el avance forzado de Calidad, que exige
// motivo. Separado de EditorBody por LOC; comparte su estado de modal.

interface Props {
  modal: EditorModal;
  caps: FlowCapabilities;
  actions: FlowActions;
  onClose: () => void;
}

export function StageActModals({ modal, caps, actions, onClose }: Props): ReactElement {
  const { showToast } = useToast();
  const flowInstanceId = caps.flowInstanceId;

  const conform = (): void => {
    if (flowInstanceId === null) {
      return;
    }
    actions.conformity.mutate(
      { flowInstanceId },
      {
        onSuccess: () => {
          onClose();
          showToast(`Conformidad registrada en ${caps.code}`);
        },
        onError: (err) =>
          showToast(stageActErrorCopy(err, 'No se pudo registrar la conformidad'), 'error'),
      },
    );
  };

  const close = (): void => {
    if (flowInstanceId === null) {
      return;
    }
    actions.closeApproval.mutate(
      { flowInstanceId },
      {
        onSuccess: () => {
          onClose();
          showToast(`Aprobación de ${caps.code} cerrada con las firmas registradas`);
        },
        onError: (err) =>
          showToast(stageActErrorCopy(err, 'No se pudo cerrar la aprobación'), 'error'),
      },
    );
  };

  const force = (reason: string): void => {
    if (modal?.kind !== 'forced' || flowInstanceId === null) {
      return;
    }
    const toStatus = modal.toStatus;
    actions.transition.mutate(
      { flowInstanceId, toStatus, reason },
      {
        onSuccess: () => {
          onClose();
          showToast(`Documento enviado a ${stageName(toStatus)}`);
        },
        onError: (err) =>
          showToast(stageActErrorCopy(err, 'No se pudo avanzar el documento'), 'error'),
      },
    );
  };

  const confirm =
    modal?.kind === 'conformity'
      ? {
          title: 'Dar conformidad',
          message:
            'Confirmas que el documento está bien elaborado. Con la conformidad de todos los revisores pasa a aprobación.',
          confirmLabel: 'Dar conformidad',
          busy: actions.conformity.isPending,
          onConfirm: conform,
        }
      : modal?.kind === 'close-approval'
        ? {
            title: 'Cerrar la aprobación',
            message:
              'Todos los aprobadores asignados ya firmaron. Se sellará la aprobación con esas firmas y su fecha de vigencia.',
            confirmLabel: 'Cerrar la aprobación',
            busy: actions.closeApproval.isPending,
            onConfirm: close,
          }
        : null;

  return (
    <>
      <ConfirmModal
        open={confirm !== null}
        title={confirm?.title ?? ''}
        message={confirm?.message ?? ''}
        confirmLabel={confirm?.confirmLabel}
        busy={confirm?.busy}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={onClose}
      />
      <ReturnModal
        open={modal?.kind === 'forced'}
        toLabel={modal?.kind === 'forced' ? stageName(modal.toStatus) : ''}
        title={
          modal?.kind === 'forced'
            ? `Enviar a ${stageName(modal.toStatus)} sin todas las conformidades`
            : undefined
        }
        confirmLabel="Enviar"
        fieldLabel="Motivo del avance (obligatorio)"
        placeholder="Explica por qué se avanza sin la conformidad de todos los revisores"
        destructive={false}
        busy={actions.transition.isPending}
        onCancel={onClose}
        onConfirm={force}
      />
    </>
  );
}
